import { type CaseDto, datePl, todayInWarsaw } from '@bqcrm/shared';
import { Button, Checkbox, Group, Modal, Stack, Text, Textarea, TextInput } from '@mantine/core';
import { useState } from 'react';
import { api } from '../../api';
import { useInvalidateProcess } from '../../components/process/ProcessTasks';
import { useCaseMutation } from '../../data';
import { notifyOk } from '../../notify';
import { clientKnownTerms, HoldEffect, useHoldPreview } from './CaseDialogs';

function useHoldMutation(c: CaseDto, path: 'hold' | 'unhold', done: () => void) {
  const refresh = useInvalidateProcess();
  return useCaseMutation(c.id, (body: unknown) => api<CaseDto>(`/cases/${c.id}/${path}`, { method: 'POST', body }), () => {
    refresh(c.id);
    done();
  });
}

/** P34: „Powiadom klienta” — e-mail z powodem i datą (bez adresu e-mail klienta — nieaktywne). */
function NotifyClient({ c, checked, onChange, description }: { c: CaseDto; checked: boolean; onChange: (v: boolean) => void; description: string }) {
  const email = c.client.emails[0];
  return (
    <Checkbox
      label={email ? `Powiadom klienta e-mailem (${email})` : 'Powiadom klienta e-mailem'}
      description={email ? description : 'Klient nie ma adresu e-mail — uzupełnij go w karcie klienta'}
      disabled={!email}
      checked={checked && !!email}
      onChange={(e) => onChange(e.currentTarget.checked)}
    />
  );
}

/** QA P7: notatka wewnętrzna — tylko w historii sprawy (nie do klienta, nie do powiadomień). */
function InternalNote({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <Textarea
      label="Notatka wewnętrzna (opcjonalnie)"
      description="Tylko w historii sprawy — nie trafia do klienta ani do powiadomień."
      placeholder="np. zaliczka wg klienta przelana, czekamy na potwierdzenie"
      autosize
      minRows={2}
      value={value}
      onChange={(e) => onChange(e.currentTarget.value)}
    />
  );
}

/** Wstrzymanie sprawy: powód, data wznowienia, skutki dla prac, powiadomienie klienta (docs/10 §16). */
export function CaseHoldDialog({ c, onClose }: { c: CaseDto; onClose: () => void }) {
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [until, setUntil] = useState('');
  // QA qa10310111 3.11: klient dostał już e-mail z odwoływanym terminem — powiadomienie domyślnie zaznaczone
  const preview = useHoldPreview(`/cases/${c.id}/hold-preview`);
  const known = clientKnownTerms(preview.data);
  const [picked, setNotify] = useState<boolean | null>(null);
  const notify = picked ?? known.length > 0;
  const email = c.client.emails[0];
  const m = useHoldMutation(c, 'hold', onClose);
  return (
    <Modal opened onClose={onClose} title="Wstrzymanie sprawy">
      <Stack>
        <Textarea
          label="Powód (widoczny dla klienta)"
          description="Trafi do e-maila dla klienta i do powiadomień biura. Ekipy dostaną tylko „Wstrzymano sprawę”."
          placeholder="np. na prośbę klienta, czekamy na decyzję banku"
          autosize
          minRows={2}
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
          data-autofocus
        />
        <InternalNote value={note} onChange={setNote} />
        <HoldEffect path={`/cases/${c.id}/hold-preview`} />
        <TextInput type="date" label="Wznowić dnia (opcjonalnie)" description="Sprawa wróci sama tego dnia" value={until} onChange={(e) => setUntil(e.currentTarget.value)} />
        <NotifyClient c={c} checked={notify} onChange={setNotify} description="Powód i planowana data wznowienia; terminy prac podane wcześniej klientowi zostaną w wiadomości odwołane." />
        {known.length > 0 && !(notify && email) && (
          <Text size="sm" c="orange">
            {known.length > 1 ? `Klient zna terminy ${known.join(', ')}` : `Klient zna termin ${known[0]}`} — bez powiadomienia będzie czekał na ekipę.
          </Text>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button loading={m.isPending} disabled={reason.trim().length < 3} onClick={() => m.mutate({ reason, note, until: until || null, notifyClient: notify && !!email })}>
            Wstrzymaj sprawę
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** Wznowienie sprawy: przed ustaloną datą — z powodem (QA D12); powiadomienie klienta. */
export function CaseUnholdDialog({ c, onClose }: { c: CaseDto; onClose: () => void }) {
  const early = !!c.hold?.until && todayInWarsaw() <= c.hold.until;
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [notify, setNotify] = useState(false);
  const m = useHoldMutation(c, 'unhold', () => {
    notifyOk('Wznowiono sprawę');
    onClose();
  });
  return (
    <Modal opened onClose={onClose} title={early ? 'Wznowienie przed terminem' : 'Wznowienie sprawy'}>
      <Stack>
        <Textarea
          label={early ? `Sprawa jest wstrzymana do ${datePl(c.hold!.until!)} — powód wcześniejszego wznowienia (widoczny dla klienta)` : 'Komentarz (widoczny dla klienta, opcjonalnie)'}
          description={early ? 'Trafi do e-maila dla klienta i do powiadomień biura; wystarczy też notatka wewnętrzna.' : 'Trafi do e-maila dla klienta i do powiadomień biura.'}
          placeholder="np. wracamy do realizacji zgodnie z ustaleniami"
          autosize
          minRows={2}
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
          data-autofocus
        />
        <InternalNote value={note} onChange={setNote} />
        <NotifyClient c={c} checked={notify} onChange={setNotify} description="Informacja o wznowieniu (z komentarzem); o terminach prac klient dowie się po ich zaplanowaniu." />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button loading={m.isPending} disabled={early && reason.trim().length < 3 && note.trim().length < 3} onClick={() => m.mutate({ reason, note, notifyClient: notify && !!c.client.emails[0] })}>
            Wznów sprawę
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
